
import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { ListCheck, FileText, Check, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/components/auth/AuthProvider";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { useTransactionalEmail } from "@/hooks/use-transactional-email";

interface SidebarCTAProps {
  centerName: string;
  email?: string;
  nhSlug: string;
  countySlug: string;
  hiqaProfileUrl?: string;
}

export function SidebarCTA({ centerName, email, nhSlug, countySlug, hiqaProfileUrl }: SidebarCTAProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isInLeads, setIsInLeads] = useState(false);
  const { sendEmail } = useTransactionalEmail();

  React.useEffect(() => {
    const checkLeads = async () => {
      if (user) {
        const { data: existingLeads, error } = await supabase
          .from('nursing_home_leads')
          .select('id')
          .eq('user_id', user.id)
          .eq('nursing_home_id', nhSlug)
          .limit(1);
        
        if (!error && existingLeads) {
          setIsInLeads(existingLeads.length > 0);
        }
      }
    };

    checkLeads();
  }, [user, nhSlug]);

  const handleJoinWaitlist = async () => {
    if (user) {
      try {
        if (isInLeads) {
          toast.info("Already in your leads", {
            description: "You've already joined the waitlist for this nursing home",
            action: {
              label: "View Dashboard",
              onClick: () => navigate("/dashboard")
            }
          });
          return;
        }
        
        const { error } = await supabase.from('nursing_home_leads').insert({
          user_id: user.id,
          nursing_home_id: nhSlug,
          nursing_home_name: centerName,
          location: countySlug,
          status: 'waitlist',
          notes: 'Added from nursing home sidebar'
        });
        
        if (error) throw error;
        
        setIsInLeads(true);

        try {
          const claimLink = `${window.location.origin}/nursing-homes/${countySlug}/${nhSlug}?action=claim`;
          
          // Send email to nursing home using their email if available
          await sendEmail({
            type: 'nursing_home_inquiry',
            data: {
              nursingHomeName: centerName,
              county: countySlug,
              message: "A family has joined your waitlist through Grandpal. They're actively looking to secure a place and would like to connect.",
              userInfo: `${user.user_metadata?.first_name || 'A user'} from Grandpal`,
              email: email || "hello@updates.grandpal.co",
              claimLink
            }
          });
          
          // Send confirmation email to the user
          await sendEmail({
            type: 'inquiry_confirmation',
            data: {
              firstName: user.user_metadata?.first_name || 'User',
              email: user.email || '',
              nursingHomeName: centerName,
              county: countySlug,
            }
          });
        } catch (emailError) {
          console.error("Failed to send nursing home inquiry email:", emailError);
          // Don't show an error toast since the main action succeeded
        }
        
        toast.success(`Added ${centerName} to your waitlist`, {
          description: "View all your saved nursing homes in your dashboard",
          action: {
            label: "View Dashboard",
            onClick: () => navigate("/dashboard")
          }
        });
      } catch (error) {
        console.error("Error adding to leads:", error);
        toast.error("Failed to add to your waitlist");
      }
    } else {
      navigate(`/waitlist?nursing_home=${encodeURIComponent(centerName)}&county=${encodeURIComponent(countySlug)}&nh_slug=${encodeURIComponent(nhSlug)}`);
      
      toast.info("Complete the form to join waitlist", {
        description: `You'll need to create an account to track ${centerName}.`
      });
    }
  };

  return (
    <div className="bg-grandpal-beige rounded-lg p-5 mb-6 shadow-sm">
      <h2 className="text-xl font-semibold mb-2">Take the Next Step</h2>
      <p className="text-gray-700 mb-4">
        Ready to learn more about {centerName}? Our advisors can help guide you through the process.
      </p>
      
      <div className="space-y-3">
        <Button 
          className={`w-full ${isInLeads 
            ? 'bg-grandpal-green-dark' 
            : 'bg-grandpal-green-dark hover:bg-grandpal-green-darkHover'} text-lg py-6`}
          size="lg"
          onClick={handleJoinWaitlist}
          disabled={isInLeads}
        >
          {isInLeads ? (
            <>
              <Check className="mr-2 h-5 w-5" />
              Added to List
            </>
          ) : (
            <>
              <ListCheck className="mr-2 h-5 w-5" />
              {user ? 'Join Waitlist' : 'Join Waitlist'}
            </>
          )}
        </Button>
        
        <div className="grid grid-cols-1 gap-3">
          <Button 
            variant="outline" 
            className="w-full border-gray-300 text-gray-700 hover:bg-gray-50"
            onClick={() => window.open('/guides/fair-deal', '_blank')}
          >
            <FileText className="mr-2 h-4 w-4" />
            Download Guide
          </Button>
        </div>
        
        <div className="flex flex-wrap gap-3 text-sm pt-2">
          {hiqaProfileUrl && (
            <a
              href={hiqaProfileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-grandpal-green-dark hover:text-grandpal-green-darkHover transition-colors"
            >
              <ExternalLink className="h-4 w-4" />
              <span>View HIQA Report</span>
            </a>
          )}
          
          <span className="text-gray-400">|</span>
          
          <a
            href="#about-section-title"
            className="inline-flex items-center gap-1 text-grandpal-green-dark hover:text-grandpal-green-darkHover transition-colors"
          >
            <span>Learn more about {centerName}</span>
          </a>
        </div>
      </div>
    </div>
  );
}
